import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <video
          className="w-full h-full object-cover hero-image"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source
            src="https://facelessly-public-artifacts.s3.eu-north-1.amazonaws.com/hf_20260223_151804_1a089168-c70e-4078-bbb5-517dcc3c447d.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/85 to-cream/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="max-w-2xl">
          {/* Private beta badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-forest/10 text-forest rounded-full px-4 py-1.5 mb-8"
            data-testid="beta-badge"
          >
            <span className="w-2 h-2 rounded-full bg-forest animate-pulse" />
            <span className="font-body text-sm font-medium">Private Beta</span>
          </motion.div>

          {/* Tagline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-ink leading-[1.1] mb-6"
            data-testid="hero-tagline"
          >
            Where Lagos{" "}
            <span className="italic text-terra">Reads</span>,{" "}
            <span className="italic text-forest">Connects</span>, and{" "}
            <span className="italic text-gold">Swaps</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="font-body text-lg text-ink-light leading-relaxed mb-10 max-w-lg"
            data-testid="hero-subtext"
          >
            Your shelf is more than a collection — it's a conversation waiting to happen.
            ReadTheRoom turns personal bookshelves into a community-wide library, starting in Lagos.
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/login"
              className="bg-terra text-cream font-body font-semibold rounded-full px-8 py-4 text-base hover:bg-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 text-center"
              data-testid="hero-cta-signin"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="border-2 border-ink/15 text-ink font-body font-medium rounded-full px-8 py-4 text-base hover:border-terra/30 hover:text-terra transition-all duration-300 text-center"
              data-testid="hero-cta-register"
            >
              Create Account
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 font-body text-xs uppercase tracking-[0.2em] text-ink-muted"
      >
        Scroll to learn more
      </motion.p>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="text-ink-muted" size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
}
